/**
 * Marker styling for live aircraft (R.1b §3.2; re-glyphed to solid silhouettes
 * in Q.1 Stage 4; #201 colour discipline, founder option B 2026-06-11). The
 * free-text aircraft_type selects a solid top-down silhouette (see
 * aircraftSilhouettes). Since #201 the marker is a constant one-colour fleet
 * treatment baked into the SVG assets; since #13 the glTF LOD model is the same
 * fleet gold (AIRCRAFT_FLEET_GOLD) — so `color` below (the role hue) now feeds
 * ONLY the side-panel role badge (the legend swatch was dropped and the glTF
 * tint switched to the fleet gold in #13). Heading is NOT baked into the image
 * — the billboard is rotated via Cesium's native `rotation` (one cached texture
 * per type, smoother than the old 5°-bucketed bake).
 *
 * The role → colour + label palette lives in the context-locked ./styles/
 * aircraftRoles module (operator-cop-doctrine §3B.8); this file owns silhouette
 * selection + billboard geometry only.
 */
import { aircraftRoleStyle } from './styles/aircraftRoles';

export type AircraftKind = 'fixed' | 'heli' | 'jet';

export interface AircraftStyle {
  /** Role hue — feeds the side-panel role badge ONLY. Since #13 the marker is
   *  the single fleet gold (AIRCRAFT_FLEET_GOLD) at every LOD: the billboard,
   *  the glTF model, and the dropped legend swatch no longer read this. */
  color: string;
  kind: AircraftKind;
  /** Human label for the side-panel role badge. */
  roleLabel: string;
  /** Billboard pixel size (slightly larger than camera markers for emphasis). */
  size: number;
}

export function roleColor(role: string | null): string {
  return aircraftRoleStyle(role).color;
}

export function roleLabel(role: string | null): string {
  return aircraftRoleStyle(role).label;
}

/**
 * The single fleet colour every live aircraft renders in (#13 founder ruling,
 * FR24-style — extends #201's "pure one-colour" silhouette to the true-3D glTF
 * LOD so a zoomed-in aircraft is the SAME gold as its billboard). Operators
 * read a uniform gold fleet on the map; per-role colour is NOT load-bearing on
 * the marker — role identity lives in the side-panel badge (clicked, not
 * scanned). This MUST match the gold baked into every silhouette SVG body (see
 * aircraftSilhouettes.ts COLOUR CONTRACT, `#fbbf24`) so the billboard and the
 * glTF model agree at the LOD swap — pinned by aircraftStyles.test.ts.
 */
export const AIRCRAFT_FLEET_GOLD = '#fbbf24';

// Common helicopter / jet type-code fragments. Best-effort: anything unmatched
// falls back to fixed-wing. Documented heuristic — not authoritative. Used as
// the silhouette fallback selector (rotor vs fixed) for unrecognised airframes.
const HELI_RE = new RegExp(
  '^(AW1|EC|H1[2-6]|H145|H125|S76|S70|B06|B41|B42|B43|B47|R44|R66|AS3|AS5|AS6|BK|UH|CH|EH10|A139|A169|A189)',
  'i',
);
const JET_RE = new RegExp('^(GLF|GL[0-9]|CL30|CL35|CL60|C25|C56|C68|E55|E50|LJ|FA[0-9]|F2TH|BE40|H25)', 'i');

export function kindFromType(typeCode: string | null, aircraftType: string | null): AircraftKind {
  const t = (typeCode ?? '').toUpperCase();
  const full = (aircraftType ?? '').toLowerCase();
  if (HELI_RE.test(t) || full.includes('helicopter') || full.includes('cyclone') || full.includes('griffon'))
    return 'heli';
  if (JET_RE.test(t) || full.includes('jet')) return 'jet';
  return 'fixed';
}

export function classifyAircraft(
  role: string | null,
  typeCode: string | null,
  aircraftType: string | null,
): AircraftStyle {
  return {
    color: roleColor(role),
    kind: kindFromType(typeCode, aircraftType),
    roleLabel: roleLabel(role),
    size: 22,
  };
}

/**
 * Marker opacity ladder per tracking tier (#245, Law 4 ambient register —
 * static, never pulsing, ≤0.85α for ambient points). The step between tiers is
 * the honest visual claim: full = live track, 0.4 = recently quiet (≤2h stale),
 * 0.22 = "last seen here / likely based nearby" — a weeks-old ghost must never
 * read as bright as an hours-old stale track. Unknown future statuses fall to
 * the dimmest visible tier rather than vanishing.
 */
export function aircraftStatusAlpha(status: string): number {
  if (status === 'live') return 1;
  if (status === 'stale') return 0.4;
  return 0.22;
}

/**
 * Marker SIZE ladder per tracking tier (#265 founder ruling, 2026-06-11). A
 * LIVE / flying aircraft renders LARGER and clearly noticeable; a based / stale
 * airframe SHRINKS relative to the historical flat 22px marker. Size REINFORCES
 * the #245 alpha ladder rather than fighting it: live = bigger + brighter (1.0α),
 * stale = smaller + dimmer (0.4α), based = smallest + faintest (0.22α). The two
 * channels always step the same direction (see the pinned monotonicity test).
 *
 * This is a STATIC channel (§3B.8 marker doctrine — size is a legal static
 * encoding; aircraft never pulse), so it stays within the Law-6 motion budget,
 * exactly like the existing zoom-responsive `scaleByDistance`.
 *
 * Two profiles tune the SPREAD, not the direction:
 *   - `cop`  — the operator COP: noticeable but contained, within the ambient /
 *     clutter law (no clutter blowout in the dense default view).
 *   - `bold` — the WALL profile + demo mode: a wider spread so a live airframe
 *     pops at room distance (the founder ruling makes the tier MANDATORY there).
 * The WALL additionally multiplies by `markerScale` (≈2×) at the call site, so
 * aircraft finally honour the room-distance scale every other wall marker uses.
 */
export type AircraftSizeProfile = 'cop' | 'bold';

/** Locked size constants (px, COP scale). Changing a value is a deliberate edit
 *  the pinned `aircraftStyles.test.ts` ladder lock forces you to make on purpose
 *  (mirrors the DEFAULT_VIEW_LAYERS + migration-verb lock pattern). */
export const AIRCRAFT_SIZE_LADDER: Record<
  AircraftSizeProfile,
  { live: number; stale: number; based: number }
> = {
  cop: { live: 26, stale: 19, based: 16 },
  bold: { live: 30, stale: 18, based: 14 },
};

export function aircraftStatusSize(status: string, profile: AircraftSizeProfile = 'cop'): number {
  const ladder = AIRCRAFT_SIZE_LADDER[profile];
  if (status === 'live') return ladder.live;
  if (status === 'stale') return ladder.stale;
  return ladder.based;
}

/**
 * Cesium billboard `rotation` (radians) that points a nose-up silhouette along a
 * compass heading. Cesium rotation is counter-clockwise-positive in screen
 * space; a compass heading is clockwise from north, so we negate. Null/invalid
 * headings render nose-up (0).
 */
export function headingToBillboardRotation(headingDeg: number | null): number {
  if (headingDeg === null || !Number.isFinite(headingDeg)) return 0;
  return (-headingDeg * Math.PI) / 180;
}
